import { createStore } from 'vuex';
import debounce from 'lodash/debounce';
import { worker } from '../workerManager';

import { userStore } from '@/modules/user/data-access';
import { studyStore } from '@/modules/study/data-access';
import { epochStore } from '@/modules/epoch/data-access';
import { reportStore } from '@/modules/report/data-access';
import { scoreSetStore } from '@/modules/score-set/data-access';
import { releaseNotesStore } from '@/modules/release-notes/data-access';

import * as mutations from './mutations';
import { SafeAny } from '@/common/types';
import { toRaw } from 'vue';

import { initialState } from './initState';

const MAX_ITEMS_SET = 10;

const debouncedSaveState = debounce((state) => {
  const intState = toRaw(state);
  const criticalSets: SafeAny = {};

  if (intState && intState.epochData && intState.epochData.sets) {
    // store the first 10 epochs of each epoch size
    Object.keys(intState.epochData.sets).forEach((epochSize) => {
      const epochData = intState.epochData.sets[epochSize];

      if (epochData) {
        const epochKeys = Object.keys(epochData).slice(0, MAX_ITEMS_SET);
        criticalSets[epochSize] = {};

        epochKeys.forEach((key) => {
          criticalSets[epochSize][key] = epochData[key];
        });
      }
    });
  }

  const criticalEpochData = {
    sets: criticalSets,
    data: intState?.epochData?.data
  };

  const criticalEpoch = {
    epochData: criticalEpochData,
    studyId: intState?.epochData?.selectedStudy,
    notch: intState?.epochData?.selectedNotch
  };

  worker.postMessage({ command: 'saveState', payload: criticalEpoch });
}, 20000); // save after 20 sec state inactivity

const vuexIndexedDB = (store: SafeAny) => {
  worker.postMessage({ command: 'getState' });

  worker.onmessage = (event) => {
    if (event.data.command === 'stateLoaded') {
      if (event.data.payload) {
        const newState = {
          ...initialState,
          epoch: { ...initialState?.epoch, epochData: { ...event.data.payload.epochData } }
        };
        store.replaceState(newState);
      } else {
        store.replaceState(initialState);
      }
    }
  };

  store.subscribe((mutation: SafeAny, state: SafeAny) => {
    const epoch = state?.epoch;
    if (epoch?.epochData) {
      // currently saving only epochData. Can store more.
      debouncedSaveState(epoch);
    }
  });
};

export const rootStore = createStore({
  plugins: [vuexIndexedDB],
  state: initialState,
  mutations,
  modules: {
    user: userStore,
    study: studyStore,
    epoch: epochStore,
    report: reportStore,
    scoreSet: scoreSetStore,
    releaseNotes: releaseNotesStore
  }
});
